import { useEffect, useMemo } from 'react';
import type {
  ObservabilitySnapshot,
  ServiceHealth,
  ServiceStatus,
} from '@/api/observability';
import { listServiceHealth } from '@/api/observability';
import { useApiQuery } from './useApiQuery';
import mockData from '@/data/mock-data.json';

const REFRESH_INTERVAL_MS = 30_000;

interface MockService {
  id: string;
  name: string;
  category: string;
  status: string;
  uptime: number;
  latencyMs: number | null;
  errorRate: number | null;
  requestsPerMin: number;
}

const statusRank: Record<ServiceStatus, number> = {
  operational: 0,
  unknown: 1,
  degraded: 2,
  down: 3,
};

const buildMockSnapshot = (): ObservabilitySnapshot => {
  const services: ServiceHealth[] = (
    mockData.services as unknown as MockService[]
  ).map((svc) => ({
    id: svc.id,
    name: svc.name,
    category: svc.category,
    status: svc.status as ServiceStatus,
    responseMs: svc.latencyMs,
    reason: null,
    checks: null,
    grafanaService: svc.id,
    uptime: svc.uptime,
    latencyMs: svc.latencyMs,
    errorRate: svc.errorRate,
    requestsPerMin: svc.requestsPerMin,
  }));

  const summary = {
    total: services.length,
    operational: 0,
    degraded: 0,
    down: 0,
    unknown: 0,
    worst: 'operational' as ServiceStatus,
  };
  for (const svc of services) {
    summary[svc.status] += 1;
    if (statusRank[svc.status] > statusRank[summary.worst]) {
      summary.worst = svc.status;
    }
  }

  return {
    services,
    infrastructure: [],
    summary,
    updatedAt: new Date().toISOString(),
    stale: false,
  };
};

interface UseServiceHealthResult {
  snapshot: ObservabilitySnapshot | undefined;
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

export const useServiceHealth = (): UseServiceHealthResult => {
  const mockSnapshot = useMemo(
    () => (import.meta.env.DEV ? buildMockSnapshot() : undefined),
    [],
  );

  const query = useApiQuery<ObservabilitySnapshot>(
    {
      queryFn: (signal) => listServiceHealth(signal),
      mockData: mockSnapshot,
    },
    [],
  );

  // Poll on a fixed cadence. The backend already serves a cached snapshot, so
  // this is a cheap read and keeps the page fresh without a websocket.
  const { reload } = query;
  useEffect(() => {
    const timer = setInterval(reload, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reload]);

  return {
    snapshot: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isUsingMockData: query.isUsingMockData,
    reload: query.reload,
  };
};
